/**
 * Tooth Numbering Utilities
 * ==========================
 * Functions for converting between Universal (ADA) and FDI tooth numbering systems.
 */

// =============================================================================
// TYPES
// =============================================================================

export type NumberingSystem = 'universal' | 'fdi' | 'palmer';

export interface ToothInfo {
  universal: number;
  fdi: string;
  palmer: string;
  name: string;
  arch: 'upper' | 'lower';
  quadrant: 1 | 2 | 3 | 4;
  type: 'molar' | 'premolar' | 'canine' | 'incisor';
  hasFurcation: boolean;
}

// =============================================================================
// CONVERSION MAPS
// =============================================================================

/**
 * Universal to FDI mapping.
 * Universal: 1-32 (US standard, starting upper right)
 * FDI: quadrant (1-4) + tooth (1-8)
 */
export const UNIVERSAL_TO_FDI: Record<number, string> = {
  // Upper Right (Quadrant 1)
  1: '18', 2: '17', 3: '16', 4: '15', 5: '14', 6: '13', 7: '12', 8: '11',
  // Upper Left (Quadrant 2)
  9: '21', 10: '22', 11: '23', 12: '24', 13: '25', 14: '26', 15: '27', 16: '28',
  // Lower Left (Quadrant 3)
  17: '38', 18: '37', 19: '36', 20: '35', 21: '34', 22: '33', 23: '32', 24: '31',
  // Lower Right (Quadrant 4)
  25: '41', 26: '42', 27: '43', 28: '44', 29: '45', 30: '46', 31: '47', 32: '48',
};

/**
 * FDI to Universal mapping.
 */
export const FDI_TO_UNIVERSAL: Record<string, number> = Object.fromEntries(
  Object.entries(UNIVERSAL_TO_FDI).map(([k, v]) => [v, parseInt(k)])
);

/**
 * Universal to Palmer mapping.
 * Palmer: uses quadrant symbols and numbers 1-8
 */
export const UNIVERSAL_TO_PALMER: Record<number, string> = {
  // Upper Right
  1: '8┘', 2: '7┘', 3: '6┘', 4: '5┘', 5: '4┘', 6: '3┘', 7: '2┘', 8: '1┘',
  // Upper Left
  9: '└1', 10: '└2', 11: '└3', 12: '└4', 13: '└5', 14: '└6', 15: '└7', 16: '└8',
  // Lower Left
  17: '┌8', 18: '┌7', 19: '┌6', 20: '┌5', 21: '┌4', 22: '┌3', 23: '┌2', 24: '┌1',
  // Lower Right
  25: '1┐', 26: '2┐', 27: '3┐', 28: '4┐', 29: '5┐', 30: '6┐', 31: '7┐', 32: '8┐',
};

// =============================================================================
// TOOTH INFO
// =============================================================================

/**
 * Complete tooth information by Universal number.
 */
export const TOOTH_INFO: Record<number, ToothInfo> = {
  // Upper Right (Quadrant 1)
  1: { universal: 1, fdi: '18', palmer: '8┘', name: 'Upper Right 3rd Molar', arch: 'upper', quadrant: 1, type: 'molar', hasFurcation: true },
  2: { universal: 2, fdi: '17', palmer: '7┘', name: 'Upper Right 2nd Molar', arch: 'upper', quadrant: 1, type: 'molar', hasFurcation: true },
  3: { universal: 3, fdi: '16', palmer: '6┘', name: 'Upper Right 1st Molar', arch: 'upper', quadrant: 1, type: 'molar', hasFurcation: true },
  4: { universal: 4, fdi: '15', palmer: '5┘', name: 'Upper Right 2nd Premolar', arch: 'upper', quadrant: 1, type: 'premolar', hasFurcation: false },
  5: { universal: 5, fdi: '14', palmer: '4┘', name: 'Upper Right 1st Premolar', arch: 'upper', quadrant: 1, type: 'premolar', hasFurcation: false },
  6: { universal: 6, fdi: '13', palmer: '3┘', name: 'Upper Right Canine', arch: 'upper', quadrant: 1, type: 'canine', hasFurcation: false },
  7: { universal: 7, fdi: '12', palmer: '2┘', name: 'Upper Right Lateral Incisor', arch: 'upper', quadrant: 1, type: 'incisor', hasFurcation: false },
  8: { universal: 8, fdi: '11', palmer: '1┘', name: 'Upper Right Central Incisor', arch: 'upper', quadrant: 1, type: 'incisor', hasFurcation: false },
  // Upper Left (Quadrant 2)
  9: { universal: 9, fdi: '21', palmer: '└1', name: 'Upper Left Central Incisor', arch: 'upper', quadrant: 2, type: 'incisor', hasFurcation: false },
  10: { universal: 10, fdi: '22', palmer: '└2', name: 'Upper Left Lateral Incisor', arch: 'upper', quadrant: 2, type: 'incisor', hasFurcation: false },
  11: { universal: 11, fdi: '23', palmer: '└3', name: 'Upper Left Canine', arch: 'upper', quadrant: 2, type: 'canine', hasFurcation: false },
  12: { universal: 12, fdi: '24', palmer: '└4', name: 'Upper Left 1st Premolar', arch: 'upper', quadrant: 2, type: 'premolar', hasFurcation: false },
  13: { universal: 13, fdi: '25', palmer: '└5', name: 'Upper Left 2nd Premolar', arch: 'upper', quadrant: 2, type: 'premolar', hasFurcation: false },
  14: { universal: 14, fdi: '26', palmer: '└6', name: 'Upper Left 1st Molar', arch: 'upper', quadrant: 2, type: 'molar', hasFurcation: true },
  15: { universal: 15, fdi: '27', palmer: '└7', name: 'Upper Left 2nd Molar', arch: 'upper', quadrant: 2, type: 'molar', hasFurcation: true },
  16: { universal: 16, fdi: '28', palmer: '└8', name: 'Upper Left 3rd Molar', arch: 'upper', quadrant: 2, type: 'molar', hasFurcation: true },
  // Lower Left (Quadrant 3)
  17: { universal: 17, fdi: '38', palmer: '┌8', name: 'Lower Left 3rd Molar', arch: 'lower', quadrant: 3, type: 'molar', hasFurcation: true },
  18: { universal: 18, fdi: '37', palmer: '┌7', name: 'Lower Left 2nd Molar', arch: 'lower', quadrant: 3, type: 'molar', hasFurcation: true },
  19: { universal: 19, fdi: '36', palmer: '┌6', name: 'Lower Left 1st Molar', arch: 'lower', quadrant: 3, type: 'molar', hasFurcation: true },
  20: { universal: 20, fdi: '35', palmer: '┌5', name: 'Lower Left 2nd Premolar', arch: 'lower', quadrant: 3, type: 'premolar', hasFurcation: false },
  21: { universal: 21, fdi: '34', palmer: '┌4', name: 'Lower Left 1st Premolar', arch: 'lower', quadrant: 3, type: 'premolar', hasFurcation: false },
  22: { universal: 22, fdi: '33', palmer: '┌3', name: 'Lower Left Canine', arch: 'lower', quadrant: 3, type: 'canine', hasFurcation: false },
  23: { universal: 23, fdi: '32', palmer: '┌2', name: 'Lower Left Lateral Incisor', arch: 'lower', quadrant: 3, type: 'incisor', hasFurcation: false },
  24: { universal: 24, fdi: '31', palmer: '┌1', name: 'Lower Left Central Incisor', arch: 'lower', quadrant: 3, type: 'incisor', hasFurcation: false },
  // Lower Right (Quadrant 4)
  25: { universal: 25, fdi: '41', palmer: '1┐', name: 'Lower Right Central Incisor', arch: 'lower', quadrant: 4, type: 'incisor', hasFurcation: false },
  26: { universal: 26, fdi: '42', palmer: '2┐', name: 'Lower Right Lateral Incisor', arch: 'lower', quadrant: 4, type: 'incisor', hasFurcation: false },
  27: { universal: 27, fdi: '43', palmer: '3┐', name: 'Lower Right Canine', arch: 'lower', quadrant: 4, type: 'canine', hasFurcation: false },
  28: { universal: 28, fdi: '44', palmer: '4┐', name: 'Lower Right 1st Premolar', arch: 'lower', quadrant: 4, type: 'premolar', hasFurcation: false },
  29: { universal: 29, fdi: '45', palmer: '5┐', name: 'Lower Right 2nd Premolar', arch: 'lower', quadrant: 4, type: 'premolar', hasFurcation: false },
  30: { universal: 30, fdi: '46', palmer: '6┐', name: 'Lower Right 1st Molar', arch: 'lower', quadrant: 4, type: 'molar', hasFurcation: true },
  31: { universal: 31, fdi: '47', palmer: '7┐', name: 'Lower Right 2nd Molar', arch: 'lower', quadrant: 4, type: 'molar', hasFurcation: true },
  32: { universal: 32, fdi: '48', palmer: '8┐', name: 'Lower Right 3rd Molar', arch: 'lower', quadrant: 4, type: 'molar', hasFurcation: true },
};

// =============================================================================
// CONVERSION FUNCTIONS
// =============================================================================

/**
 * Convert Universal tooth number to FDI notation.
 */
export function universalToFDI(universal: number): string {
  return UNIVERSAL_TO_FDI[universal] || '';
}

/**
 * Convert FDI notation to Universal tooth number.
 */
export function fdiToUniversal(fdi: string): number {
  return FDI_TO_UNIVERSAL[fdi] || 0;
}

/**
 * Convert Universal tooth number to Palmer notation.
 */
export function universalToPalmer(universal: number): string {
  return UNIVERSAL_TO_PALMER[universal] || '';
}

/**
 * Format tooth number in specified system.
 */
export function formatToothNumber(universal: number, system: NumberingSystem): string {
  switch (system) {
    case 'universal':
      return String(universal);
    case 'fdi':
      return universalToFDI(universal);
    case 'palmer':
      return universalToPalmer(universal);
    default:
      return String(universal);
  }
}

/**
 * Get tooth info by Universal number.
 */
export function getToothInfo(universal: number): ToothInfo | null {
  return TOOTH_INFO[universal] || null;
}

/**
 * Check if tooth is a molar (has furcation possibility).
 */
export function isMolar(universal: number): boolean {
  return TOOTH_INFO[universal]?.type === 'molar' || false;
}

/**
 * Check if tooth has furcation (molars).
 */
export function hasFurcation(universal: number): boolean {
  return TOOTH_INFO[universal]?.hasFurcation || false;
}

/**
 * Get teeth by arch.
 */
export function getTeethByArch(arch: 'upper' | 'lower'): number[] {
  if (arch === 'upper') {
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
  }
  return [32, 31, 30, 29, 28, 27, 26, 25, 24, 23, 22, 21, 20, 19, 18, 17];
}

/**
 * Get teeth by quadrant.
 */
export function getTeethByQuadrant(quadrant: 1 | 2 | 3 | 4): number[] {
  switch (quadrant) {
    case 1: return [1, 2, 3, 4, 5, 6, 7, 8];
    case 2: return [9, 10, 11, 12, 13, 14, 15, 16];
    case 3: return [17, 18, 19, 20, 21, 22, 23, 24];
    case 4: return [25, 26, 27, 28, 29, 30, 31, 32];
  }
}

/**
 * Get adjacent teeth.
 */
export function getAdjacentTeeth(universal: number): { mesial: number | null; distal: number | null } {
  const info = TOOTH_INFO[universal];
  if (!info) return { mesial: null, distal: null };
  
  // Same arch logic
  if (info.arch === 'upper') {
    if (universal === 8) return { mesial: 9, distal: 7 };
    if (universal === 9) return { mesial: 8, distal: 10 };
    return {
      mesial: universal < 8 ? universal + 1 : (universal < 16 ? universal + 1 : null),
      distal: universal > 1 && universal <= 8 ? universal - 1 : (universal > 9 ? universal - 1 : null),
    };
  } else {
    if (universal === 24) return { mesial: 25, distal: 23 };
    if (universal === 25) return { mesial: 24, distal: 26 };
    return {
      mesial: universal > 25 ? universal - 1 : (universal > 17 ? universal - 1 : null),
      distal: universal < 32 && universal >= 25 ? universal + 1 : (universal < 24 ? universal + 1 : null),
    };
  }
}
